import { forwardRef } from "react";

const INK = "#1E1E1E";
const TEXT = "#232323";
const SEC = "#6B6B6B";
const GOLD = "#DB960D";
const BORDER = "#ECECE8";

export const MANUAL_LABELS = {
  nl: { name: "Nederlands", guide: "Installatiehandleiding", steps: "Stappen", step: "Stap", tips: "Belangrijk & tips", support: "Support & documentatie", contents: "Inhoud", materials: "Materiaal", specifications: "Specificaties", disclaimer: "Disclaimer", warranty: "Garantie" },
  fr: { name: "Français", guide: "Guide d'installation", steps: "Étapes", step: "Étape", tips: "Important & astuces", support: "Support & documentation", contents: "Sommaire", materials: "Matériau", specifications: "Spécifications", disclaimer: "Avertissement", warranty: "Garantie" },
  en: { name: "English", guide: "Installation guide", steps: "Steps", step: "Step", tips: "Important & tips", support: "Support & documentation", contents: "Contents", materials: "Material", specifications: "Specifications", disclaimer: "Disclaimer", warranty: "Warranty" },
  de: { name: "Deutsch", guide: "Installationsanleitung", steps: "Schritte", step: "Schritt", tips: "Wichtig & Tipps", support: "Support & Dokumentation", contents: "Inhalt", materials: "Material", specifications: "Spezifikationen", disclaimer: "Haftungsausschluss", warranty: "Garantie" },
};

const W = 559; // A5 width @ ~96dpi
const PADX = 40;

const sectionLabel = (txt) => (
  <div style={{ fontFamily: "'Roboto Mono', monospace", fontSize: 10, letterSpacing: "0.25em", textTransform: "uppercase", color: SEC, marginBottom: 16 }}>{txt}</div>
);

const StepRow = ({ n, step, stepWord }) => (
  <div style={{ display: "flex", gap: 14, alignItems: "flex-start" }}>
    <div style={{ width: 30, height: 30, flexShrink: 0, background: INK, color: "#FFFFFF", borderRadius: 4, display: "flex", alignItems: "center", justifyContent: "center", fontFamily: "'Outfit', sans-serif", fontWeight: 700, fontSize: 14, lineHeight: 1 }}>
      {String(n).padStart(2, "0")}
    </div>
    <div style={{ flex: 1, minWidth: 0 }}>
      {step.title && <h3 style={{ fontFamily: "'Outfit', sans-serif", fontSize: 15, fontWeight: 600, color: TEXT, margin: "3px 0 0" }}>{step.title}</h3>}
      {step.description && <p style={{ color: SEC, fontSize: 12, lineHeight: 1.6, margin: "6px 0 0", whiteSpace: "pre-line" }}>{step.description}</p>}
      {step.image && <img src={step.image} alt={step.title || `${stepWord} ${n}`} style={{ display: "block", maxWidth: "100%", borderRadius: 8, marginTop: 10, border: `1px solid ${BORDER}` }} />}
    </div>
  </div>
);

const infoBody = { color: SEC, fontSize: 12, lineHeight: 1.6, margin: "6px 0 0", whiteSpace: "pre-line" };

export const ManualCoverPreview = ({ productName, cover, guide, langCodes = ["nl"] }) => (
  <div data-testid="manual-cover-preview" style={{ width: W, background: "#FFFFFF", color: TEXT, fontFamily: "'Manrope', sans-serif", padding: "52px 44px", display: "flex", flexDirection: "column", alignItems: "center", textAlign: "center", border: `1px solid ${BORDER}` }}>
    <div style={{ fontFamily: "'Roboto Mono', monospace", fontSize: 11, letterSpacing: "0.3em", textTransform: "uppercase", color: GOLD }}>{guide}</div>
    <h1 style={{ fontFamily: "'Outfit', sans-serif", fontSize: 34, fontWeight: 700, lineHeight: 1.12, color: TEXT, margin: "14px 0 0" }}>{productName || "Productnaam"}</h1>
    {cover && <img src={cover} alt="cover" style={{ display: "block", width: "80%", maxHeight: 300, objectFit: "contain", margin: "30px 0 20px" }} />}
    <div style={{ marginTop: cover ? 8 : 40, fontFamily: "'Roboto Mono', monospace", fontSize: 13, letterSpacing: "0.28em", color: GOLD, fontWeight: 700 }}>
      {langCodes.map((c) => c.toUpperCase()).join("   ·   ")}
    </div>
    <div style={{ marginTop: 26, fontFamily: "'Cascadia Code', 'Courier New', monospace", fontWeight: 700, fontStyle: "italic", fontSize: 20, color: TEXT }}>KANN<span style={{ color: GOLD }}>.</span></div>
  </div>
);

const ManualPreview = forwardRef(({ manual, labels = MANUAL_LABELS.nl }, ref) => {
  const { product_name, intro, cover, steps = [], sections = [], warnings, contact, materials, specifications, disclaimer, warranty } = manual;

  return (
    <div style={{ width: W }}>
      {/* language indicator bar (per-page version is drawn in the PDF) */}
      <div style={{ background: INK, color: "#fff", padding: "6px 16px", display: "flex", alignItems: "center", justifyContent: "space-between", fontFamily: "'Roboto Mono', monospace", fontSize: 10, letterSpacing: "0.2em", textTransform: "uppercase" }}>
        <span>{labels.name}</span>
        <span style={{ color: GOLD, fontStyle: "italic", fontFamily: "'Cascadia Code', monospace", fontWeight: 700, letterSpacing: 0 }}>KANN.</span>
      </div>

      <div ref={ref} data-testid="manual-preview" style={{ width: W, background: "#FFFFFF", color: TEXT, fontFamily: "'Manrope', sans-serif" }}>
        {/* Header */}
        <div data-pdf-block style={{ position: "relative", borderBottom: cover ? "none" : `1px solid ${BORDER}` }}>
          <div style={{ position: "relative", padding: `34px ${PADX}px 28px` }}>
            <div style={{ position: "absolute", inset: 0, backgroundImage: "url(\"data:image/svg+xml," + encodeURIComponent("<svg xmlns='http://www.w3.org/2000/svg' width='20' height='20'><circle cx='2' cy='2' r='1' fill='#BDBDBD'/></svg>") + "\")", opacity: 0.5 }} />
            <div style={{ position: "relative" }}>
              <div style={{ fontFamily: "'Roboto Mono', monospace", fontSize: 10, letterSpacing: "0.3em", textTransform: "uppercase", color: GOLD }}>{labels.guide}</div>
              <h1 style={{ fontFamily: "'Outfit', sans-serif", fontSize: 30, fontWeight: 600, lineHeight: 1.12, color: TEXT, margin: "8px 0 0" }}>{product_name || "Productnaam"}</h1>
              {intro && <p style={{ color: SEC, fontSize: 12.5, lineHeight: 1.55, margin: "10px 0 0", whiteSpace: "pre-line" }}>{intro}</p>}
            </div>
          </div>
        </div>

        {/* Cover — full width, as large as possible (1:1) */}
        {cover && (
          <div data-pdf-block style={{ borderBottom: `1px solid ${BORDER}`, background: "#FFFFFF" }}>
            <img src={cover} alt="cover" style={{ display: "block", width: "100%", height: "auto", objectFit: "contain" }} />
          </div>
        )}

        {/* Product info: materials & specifications */}
        {(materials || specifications) && (
          <div data-pdf-block style={{ padding: `26px ${PADX}px 6px` }}>
            {materials && (
              <div style={{ marginBottom: specifications ? 20 : 0 }}>
                {sectionLabel(labels.materials)}
                <p style={infoBody}>{materials}</p>
              </div>
            )}
            {specifications && (
              <div>
                {sectionLabel(labels.specifications)}
                <p style={infoBody}>{specifications}</p>
              </div>
            )}
          </div>
        )}

        {/* Main steps */}
        {steps.length === 0 && sections.length === 0 && (
          <div data-pdf-block style={{ padding: `26px ${PADX}px` }}>{sectionLabel(labels.steps)}<p style={{ color: SEC, fontSize: 12 }}>—</p></div>
        )}
        {steps.map((s, i) => (
          <div key={`m-${i}`} data-pdf-block style={{ padding: i === 0 ? `26px ${PADX}px 18px` : `4px ${PADX}px 18px` }}>
            {i === 0 && sectionLabel(labels.steps)}
            <StepRow n={i + 1} step={s} stepWord={labels.step} />
          </div>
        ))}

        {/* Sections */}
        {sections.map((sec, si) => (
          <div key={`s-${si}`}>
            {(sec.steps || []).length === 0 ? (
              <div data-pdf-block style={{ padding: `12px ${PADX}px 18px` }}><div style={{ borderTop: `1px solid ${BORDER}`, paddingTop: 20 }}>{sectionLabel(sec.title || "—")}</div></div>
            ) : (
              (sec.steps || []).map((s, i) => (
                <div key={`s-${si}-${i}`} data-pdf-block style={{ padding: i === 0 ? `12px ${PADX}px 18px` : `4px ${PADX}px 18px` }}>
                  {i === 0 && <div style={{ borderTop: `1px solid ${BORDER}`, paddingTop: 20 }}>{sectionLabel(sec.title || "—")}</div>}
                  <StepRow n={i + 1} step={s} stepWord={labels.step} />
                </div>
              ))
            )}
          </div>
        ))}

        {/* Warnings */}
        {warnings && (
          <div data-pdf-block style={{ padding: `6px ${PADX}px 22px` }}>
            <div style={{ background: "#FCF6E9", borderLeft: `4px solid ${GOLD}`, borderRadius: 8, padding: "14px 18px" }}>
              <div style={{ color: GOLD, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.15em", fontSize: 10.5, fontFamily: "'Roboto Mono', monospace" }}>{labels.tips}</div>
              <p style={{ color: TEXT, fontSize: 12, lineHeight: 1.55, margin: "8px 0 0", whiteSpace: "pre-line" }}>{warnings}</p>
            </div>
          </div>
        )}

        {/* Disclaimer */}
        {disclaimer && (
          <div data-pdf-block style={{ padding: `6px ${PADX}px 16px` }}>
            {sectionLabel(labels.disclaimer)}
            <p style={{ color: SEC, fontSize: 10.5, lineHeight: 1.55, whiteSpace: "pre-line" }}>{disclaimer}</p>
          </div>
        )}

        {/* Warranty */}
        {warranty && (
          <div data-pdf-block style={{ padding: `2px ${PADX}px 20px` }}>
            {sectionLabel(labels.warranty)}
            <p style={{ color: TEXT, fontSize: 12, lineHeight: 1.55, whiteSpace: "pre-line", margin: 0 }}>{warranty}</p>
          </div>
        )}

        {/* Footer */}
        <div data-pdf-block data-pdf-footer style={{ borderTop: `1px solid ${BORDER}`, background: "#F4F4F4", padding: `18px ${PADX}px`, display: "flex", alignItems: "center", justifyContent: "space-between", gap: 16 }}>
          <div>
            <div style={{ fontFamily: "'Cascadia Code', 'Courier New', monospace", fontWeight: 700, fontStyle: "italic", fontSize: 16, color: TEXT }}>KANN<span style={{ color: GOLD }}>.</span></div>
            {contact && <div style={{ color: SEC, fontSize: 11, lineHeight: 1.5, marginTop: 5, whiteSpace: "pre-line" }}>{contact}</div>}
          </div>
          <div style={{ fontFamily: "'Roboto Mono', monospace", fontSize: 9, letterSpacing: "0.2em", textTransform: "uppercase", color: SEC, textAlign: "right" }}>{labels.support}</div>
        </div>
      </div>
    </div>
  );
});

export default ManualPreview;
